<template>
  <help-dialog title="Project Dashboard" content-class="dialog-top-right">
    <template #activator="{ openDialog }">
      <v-btn
        variant="text"
        icon="mds:help"
        class="mr-4"
        @click="openDialog"
      />
    </template>

    <template #default>
      <h3 class="mb-1 text-black">Welcome to Your Project Dashboard!</h3>
      <p class="text-grey-darken-3">
        Here, you can manage all workflows and team members for this project.
        The currently selected project is displayed at the top of the screen, next to the organisation name and the Home icon <v-icon size="small">mds:home</v-icon>.
        <br><br>
        To return to the Home Dashboard, click the Home icon <v-icon size="small">mds:home</v-icon> in the top-left corner.
      </p>

      <br><br>

      <h3 class="mb-1 text-black">Starred Workflows</h3>
      <p class="text-grey-darken-3">
        At the top, you’ll see any workflows you’ve starred for quick access (if you have any).
        To star or unstar a workflow, hover over its thumbnail and use the menu that appears.
      </p>

      <br><br>

      <h3 class="mb-1 text-black">Other / All Workflows</h3>
      <p class="text-grey-darken-3">
        Below the starred section (if any workflows are starred), you’ll find all other workflows associated with this project.
        Click any workflow to open it. To create a new workflow, click <b>+ New Workflow</b>.
      </p>

      <br><br>

      <h3 class="mb-1 text-black">Project Members</h3>
      <p class="text-grey-darken-3">
        Below the workflows, you can view all members of this project. Use the <b>Invite Members</b> button to invite new members.
        <br><br>
        Team members <b>CAN</b>:
      </p>
      <ul class="ml-4">
        <li>Access and edit all workflows in the project.</li>
        <li>Add and delete workflows.</li>
        <li>Invite other team members to the project.</li>
        <li>Leave the project by removing themselves from it.</li>
      </ul>
      <br>
      <p class="text-grey-darken-3">
        Team members <b>CANNOT</b>:
      </p>
      <ul class="ml-4">
        <li>Delete the project or change the owner.</li>
        <li>Remove <b>other</b> team members from the project. Only the project owner can remove other members.</li>
        <li>Unlock a workflow if it is locked for changes (i.e., its status is set to Completed under Settings). Only the owner can unlock a workflow. A locked workflow also cannot be deleted.</li>
      </ul>
      <br><br>
      <h3 class="mb-1 text-black">Quick Tips</h3>
      <ul class="ml-4">
        <li><b>Edit/Copy Workflows:</b> Hover over any workflow to display its context menu. From there, you can copy, edit, or delete the workflow.</li>
        <li><b>Star Workflows:</b> Keep your most important workflows at the top of the page by starring them for faster access.</li>
        <li><b>Invite Members:</b> Easily collaborate by inviting team members directly to the project.</li>
        <li><b>Remove Members:</b> The project owner can remove other team members using the actions menu in the member list. Team members can leave the project themselves with <b>Leave project</b> in the same menu.</li>
      </ul>
      <br><br>
      <template v-if="brand.links.methodGuides">
        <h3 class="mb-1 text-black">Learn More</h3>
        <p class="text-grey-darken-3">
          Our walkthroughs provide a complete guide to each of our approaches. Select a link below and dive in!
        </p>
        <br>
        <ol class="pl-10 text-black">
          <li v-if="brand.links.methodGuides.eventStorming">
            <a
              :href="brand.links.methodGuides.eventStorming"
              target="_blank"
              class="text-blue-accent-4"
            >
              <b>Event Storming</b>
            </a>
          </li>
          <li v-if="brand.links.methodGuides.eventModeling">
            <a
              :href="brand.links.methodGuides.eventModeling"
              target="_blank"
              class="text-blue-accent-4"
            >
              <b>Event Modeling</b>
            </a>
          </li>
          <li v-if="brand.links.methodGuides.processMapping">
            <a
              :href="brand.links.methodGuides.processMapping"
              target="_blank"
              class="text-blue-accent-4"
            >
              <b>Simple Process Mapping</b>
            </a>
          </li>
          <li v-if="brand.links.methodGuides.userStoryMapping">
            <a
              :href="brand.links.methodGuides.userStoryMapping"
              target="_blank"
              class="text-blue-accent-4"
            >
              <b>User Story Mapping</b>
            </a>
          </li>
          <li v-if="brand.links.methodGuides.domainDrivenDesign">
            <a
              :href="brand.links.methodGuides.domainDrivenDesign"
              target="_blank"
              class="text-blue-accent-4"
            >
              <b>Domain Driven Design (DDD)</b>
            </a>
          </li>
        </ol>
      </template>
      <br><br>
    </template>
  </help-dialog>
</template>

<script setup>
import HelpDialog from '@/components/HelpDialog.vue'
import { useBrand } from '@/composables/useBrand'

const brand = useBrand()
</script>
